import { Mesh, Ray, Vector3 } from "@babylonjs/core"
import { isEqual } from "lodash"
import createGraph from "ngraph.graph"
import { nba } from "ngraph.path"
import { MazeStore } from "./MazeStore"
import { MazeWalls, Wall } from "./MazeWalls"

export class MazeGrid {

    static readonly GRID_SIZE = 10
    static readonly HALF_GRID_SIZE = MazeGrid.GRID_SIZE / 2

    static getPositionOnGrid = (coor: Coor) => ({
        x: (coor.x * MazeGrid.GRID_SIZE - MazeWalls.WALLS_WIDE * MazeGrid.GRID_SIZE / 2),
        z: (coor.z * MazeGrid.GRID_SIZE)
    })

    static getCoorForVectorPosition = (vector: Vector3): Coor => {
        const x = Math.floor(vector.x / MazeGrid.GRID_SIZE) + 5
        const z = Math.ceil(vector.z / MazeGrid.GRID_SIZE)
        return {x, z}
    }

    static getMidPositionOnGrid = (coor: Coor): Vector3 => {
        const runnerPosition = MazeGrid.getPositionOnGrid(coor)
        return new Vector3(runnerPosition.x, 5, runnerPosition.z - MazeGrid.HALF_GRID_SIZE)
    }

    static rayFromPlayerReachesCig = (cig: Mesh) => {
        const {cameras, scene} = MazeStore.instance
        const cameraPosition = cameras.freeCamera.position
        const cigPosition = cig.position

        const ray = Ray.CreateNewFromTo(cameraPosition, cigPosition)

        const picked = scene.pickWithRay(ray)
        return !picked.hit
    }

    static createMazePathingGraph = (wallsLong: number, walls: Array<Wall>) => {
        const graph = createGraph()

        for (let x = 0; x < 9; x++) {
            // 0-8 -- 9 x positions on the grid
            for (let z = 1; z < wallsLong + 1; z++) {
                // 1-20 -- 20 z positions on the grid
                const connections = MazeGrid.findConnectingSpaces({x, z}, walls)
                connections.forEach(connection => graph.addLink(CoorUtils.print({x, z}), CoorUtils.print(connection)))
            }
        }

        const pathFinder = nba(graph)

        return (from: Coor, to: Coor): Array<Coor> => {
            if (isEqual(from, to)) {
                // They're equal, so path finding won't help, just go to it!
                return [to]
            } else {
                const path: Array<Coor> = pathFinder.find(CoorUtils.print(from), CoorUtils.print(to))
                                                    .map(node => CoorUtils.unprint(node.id))
                                                    .reverse()
                // Don't include the from coor
                path.shift()
                return path
            }
        }
    }

    static findConnectingSpaces = (from: Coor, walls: Array<Wall>): Array<Coor> => {
        const connectors: Array<Coor> = []
        const {x, z} = from

        type FoundWall = Wall & { found?: boolean }

        const northWall: FoundWall = {x, z}
        const southWall: FoundWall = {x, z: z - 1}
        const westWall: FoundWall = {x, z: z - 1, vertical: true}
        const eastWall: FoundWall = {x: x + 1, z: z - 1, vertical: true}
        const wallsToCheck = [
            northWall,
            southWall,
            westWall,
            eastWall,
        ]

        walls.forEach(wall => {
            wallsToCheck.forEach(toCheck => {
                if (isEqual(wall, toCheck)) {
                    toCheck.found = true
                }
            })
        })

        if (!eastWall.found) {
            connectors.push({x: x + 1, z})
        }
        if (!westWall.found) {
            connectors.push({x: x - 1, z})
        }
        if (!southWall.found) {
            connectors.push({x, z: z - 1})
        }
        if (!northWall.found) {
            connectors.push({x, z: z + 1})
        }

        return connectors
    }
}

export interface Coor {
    x: number
    z: number
}

export class CoorUtils {
    static print = (coor: Coor) => `(${coor.x},${coor.z})`
    static unprint = (coorPrinted: string | number) => {
        const asArray = coorPrinted.toString()
            .replace("(", "")
            .replace(")", "")
            .split(",")
        return {x: Number(asArray[0]), z: Number(asArray[1])}
    }
}
